import React, { useState, useEffect, useRef, ComponentType } from 'react';
import { AppState, LogBox, ActivityIndicator, View, Platform } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator, StackScreenProps, TransitionPresets } from '@react-navigation/stack';
import * as Linking from 'expo-linking';
import { SafeAreaProvider } from 'react-native-safe-area-context';

// Web için react-native-screens'i devre dışı bırak (React error #130'u önler)
// Webpack config'de 'react-native-screens': false olarak ayarlanmış
// Bu @react-navigation/stack'in vanilla mode'da çalışmasını sağlar

// Providers
import { AuthProvider, useAuth } from './context/AuthContext';
import { KycProvider } from './context/KycContext';
import { LocalizationProvider } from './context/LocalizationContext';
import { ToastProvider } from './context/ToastContext';

// Components
import Toast from './components/Toast';
import AppLoadingScreen from './components/AppLoadingScreen';
import InstallPWAButton from './components/InstallPWAButton';
import { COLORS } from './styles/commonStyles';
import { useNotifications } from './hooks/useNotifications';
import AppLockScreen from './components/AppLockScreen';
import { useApiHealthCheck } from './hooks/useApiHealthCheck';
import logger from './utils/logger';

// Lazy loading helper for React Native - uses state-based lazy loading
// React 19 uyumluluğu için tip tanımları güncellendi
const createLazyComponent = <P extends object = any>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  LoadingComponent?: ComponentType<any>
): React.ComponentType<P> => {
  const LazyWrapper: React.FC<P> = (props: P) => {
    const [Component, setComponent] = useState<ComponentType<P> | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    
    useEffect(() => {
      importFn()
        .then((module) => {
          setComponent(() => module.default);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Failed to lazy load component:', error);
          setIsLoading(false);
        });
    }, []);
    
    if (isLoading) {
      const Fallback = LoadingComponent || (() => (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.slate100 }}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      ));
      return <Fallback />;
    }
    
    if (!Component) {
      return (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.slate100 }}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      );
    }
    
    return <Component {...props} />;
  };
  
  return LazyWrapper;
};

// Lazy load screens for better performance
// Auth screens - only load when needed
const StitchLoginScreen = createLazyComponent(() => import('./components/stitch/screens/auth/StitchLoginScreen'));
const StitchRegisterScreen = createLazyComponent(() => import('./components/stitch/screens/auth/StitchRegisterScreen'));
const Stitch2FASetupScreen = createLazyComponent(() => import('./components/stitch/screens/auth/Stitch2FASetupScreen'));
const StitchForgotPasswordScreen = createLazyComponent(() => import('./components/stitch/screens/auth/StitchForgotPasswordScreen'));

// Special flow screens
const VerificationCodeScreen = createLazyComponent(() => import('./components/steps/VerificationCodeScreen'));
const ResetPasswordScreen = createLazyComponent(() => import('./components/steps/ResetPasswordScreen'));
const AcceptInvitationScreen = createLazyComponent(() => import('./components/steps/AcceptInvitationScreen'));
const InviteAcceptScreen = createLazyComponent(() => import('./components/InviteAcceptScreen'));

// KYC screens - heavy components, lazy load all
const StitchWelcomeScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchWelcomeScreen'));
const StitchCountrySelectScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchCountrySelectScreen'));
const StitchLegalConsentScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchLegalConsentScreen'));
const StitchDocumentTypeScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchDocumentTypeScreen'));
const StitchDocumentUploadScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchDocumentUploadScreen'));
const StitchOcrConfirmationScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchOcrConfirmationScreen'));
const StitchLivenessReadyScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchLivenessReadyScreen'));
const StitchLivenessCheckScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchLivenessCheckScreen'));
const StitchAnalysisScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchAnalysisScreen'));
const StitchCompletionScreen = createLazyComponent(() => import('./components/stitch/screens/kyc/StitchCompletionScreen'));

// User screens - lazy load for better initial load time
const StitchUserProfileScreen = createLazyComponent(() => import('./components/stitch/screens/user/StitchUserProfileScreen'));
const StitchNotificationCenterScreen = createLazyComponent(() => import('./components/stitch/screens/user/StitchNotificationCenterScreen'));
const StitchUserSettingsScreen = createLazyComponent(() => import('./components/stitch/screens/user/StitchUserSettingsScreen'));
const StitchSubmissionHistoryScreen = createLazyComponent(() => import('./components/stitch/screens/user/StitchSubmissionHistoryScreen'));
const StitchHelpSupportScreen = createLazyComponent(() => import('./components/stitch/screens/user/StitchHelpSupportScreen'));

// Admin screens - lazy load as they're rarely accessed
const StitchSuperAdminPanel = createLazyComponent(() => import('./components/stitch/screens/admin/StitchSuperAdminPanel'));
const StitchCompanyAdminPanel = createLazyComponent(() => import('./components/stitch/screens/admin/StitchCompanyAdminPanel'));

// Admin feature screens - lazy loaded
const AdminUsersScreen = createLazyComponent(() => import('./components/stitch/screens/admin/AdminUsersScreen'));
const AdminCompaniesScreen = createLazyComponent(() => import('./components/stitch/screens/admin/AdminCompaniesScreen'));
const AdminSubmissionsScreen = createLazyComponent(() => import('./components/stitch/screens/admin/AdminSubmissionsScreen'));
const SystemHealthScreen = createLazyComponent(() => import('./components/stitch/screens/admin/SystemHealthScreen'));
const AlertsScreen = createLazyComponent(() => import('./components/stitch/screens/admin/AlertsScreen'));
const SystemSettingsScreen = createLazyComponent(() => import('./components/stitch/screens/admin/SystemSettingsScreen'));
const LanguageSettingsScreen = createLazyComponent(() => import('./components/stitch/screens/admin/LanguageSettingsScreen'));
const AnalyticsScreen = createLazyComponent(() => import('./components/stitch/screens/admin/AnalyticsScreen'));
const SecurityLogsScreen = createLazyComponent(() => import('./components/stitch/screens/admin/SecurityLogsScreen'));
const CompanyUsersScreen = createLazyComponent(() => import('./components/stitch/screens/admin/CompanyUsersScreen'));
const CompanySettingsScreen = createLazyComponent(() => import('./components/stitch/screens/admin/CompanySettingsScreen'));
const CreditsScreen = createLazyComponent(() => import('./components/stitch/screens/admin/CreditsScreen'));
const EmailTemplatesScreen = createLazyComponent(() => import('./components/stitch/screens/admin/EmailTemplatesScreen'));

// Ignore specific warnings that are noisy in Expo Go
LogBox.ignoreLogs(["[react-native-gesture-handler] Seems like you're using an old API with gesture components, check out new Gestures system!"]);

const Stack = createStackNavigator();

const AuthStack = () => (
  // @ts-expect-error - React Navigation React 19 uyumsuzluğu: id property gerekli
  <Stack.Navigator 
    screenOptions={{
      headerShown: false,
      ...TransitionPresets.SlideFromRightIOS,
      transitionSpec: {
        open: {
          animation: 'timing',
          config: {
            duration: 450, // Daha smooth animasyon için süre artırıldı
            useNativeDriver: Platform.OS !== 'web',
          },
        },
        close: {
          animation: 'timing',
          config: {
            duration: 400, // Daha smooth animasyon için süre artırıldı
            useNativeDriver: Platform.OS !== 'web',
          },
        },
      },
    }}
  >
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu, React Navigation güncellemesi bekleniyor */}
    <Stack.Screen name="Login" component={StitchLoginScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="Register" component={StitchRegisterScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="InviteAccept" component={InviteAcceptScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="Verify" component={VerificationCodeScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="ForgotPassword" component={StitchForgotPasswordScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="ResetPassword" component={ResetPasswordScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="AcceptInvitation" component={AcceptInvitationScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="2FASetup" component={Stitch2FASetupScreen} />
  </Stack.Navigator>
);

const KycStack = () => (
  // @ts-expect-error - React Navigation React 19 uyumsuzluğu: id property gerekli
  <Stack.Navigator 
    screenOptions={{
      headerShown: false,
      ...TransitionPresets.SlideFromRightIOS,
      transitionSpec: {
        open: {
          animation: 'timing',
          config: {
            duration: 450, // Daha smooth animasyon için süre artırıldı
            useNativeDriver: Platform.OS !== 'web',
          },
        },
        close: {
          animation: 'timing',
          config: {
            duration: 400, // Daha smooth animasyon için süre artırıldı
            useNativeDriver: Platform.OS !== 'web',
          },
        },
      },
    }}
  >
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycWelcome" component={StitchWelcomeScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycCountrySelect" component={StitchCountrySelectScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycLegalConsent" component={StitchLegalConsentScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycDocSelect" component={StitchDocumentTypeScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycDocUpload" component={StitchDocumentUploadScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycOcrConfirm" component={StitchOcrConfirmationScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycLivenessReady" component={StitchLivenessReadyScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycLiveness" component={StitchLivenessCheckScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycAnalysis" component={StitchAnalysisScreen} />
    {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
    <Stack.Screen name="KycComplete" component={StitchCompletionScreen} />
  </Stack.Navigator>
);

const AdminStack = () => {
  const { user } = useAuth();
  const AdminComponent = user?.role === 'super-admin' ? StitchSuperAdminPanel : StitchCompanyAdminPanel;
  const isSuperAdmin = user?.role === 'super-admin';
  
  return (
    // @ts-expect-error - React Navigation React 19 uyumsuzluğu: id property gerekli
    <Stack.Navigator 
      screenOptions={{
        headerShown: false,
        ...TransitionPresets.SlideFromRightIOS,
        transitionSpec: {
          open: {
            animation: 'timing',
            config: {
              duration: 300,
              useNativeDriver: Platform.OS !== 'web',
            },
          },
          close: {
            animation: 'timing',
            config: {
              duration: 250,
              useNativeDriver: Platform.OS !== 'web',
            },
          },
        },
      }}
    >
      {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
      <Stack.Screen name="AdminDashboard" component={AdminComponent} />
      
      {/* Common Admin Screens */}
      {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
      <Stack.Screen name="AdminSubmissions" component={AdminSubmissionsScreen} />
      {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
      <Stack.Screen name="Analytics" component={AnalyticsScreen} />
      
      {/* Super Admin Only Screens */}
      {isSuperAdmin && (
        <>
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="AdminUsers" component={AdminUsersScreen} />
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="AdminCompanies" component={AdminCompaniesScreen} />
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="SystemHealth" component={SystemHealthScreen} />
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="Alerts" component={AlertsScreen} />
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="SystemSettings" component={SystemSettingsScreen} />
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="LanguageSettings" component={LanguageSettingsScreen} />
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="SecurityLogs" component={SecurityLogsScreen} />
        </>
      )}
      
      {/* Company Admin Only Screens */}
      {!isSuperAdmin && (
        <>
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="CompanyUsers" component={CompanyUsersScreen} />
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="CompanySettings" component={CompanySettingsScreen} />
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="Credits" component={CreditsScreen} />
          {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
          <Stack.Screen name="EmailTemplates" component={EmailTemplatesScreen} />
        </>
      )}
      
      {/* Shared screens for both admin types */}
      {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
      <Stack.Screen name="Submissions" component={AdminSubmissionsScreen} />
    </Stack.Navigator>
  );
};

const UserStack = () => (
    // @ts-expect-error - React Navigation React 19 uyumsuzluğu: id property gerekli
    <Stack.Navigator 
      screenOptions={{
        headerShown: false,
        ...TransitionPresets.SlideFromRightIOS,
        transitionSpec: {
          open: {
            animation: 'timing',
            config: {
              duration: 300,
              useNativeDriver: Platform.OS !== 'web',
            },
          },
          close: {
            animation: 'timing',
            config: {
              duration: 250,
              useNativeDriver: Platform.OS !== 'web',
            },
          },
        },
      }}
    >
        {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
        <Stack.Screen name="Profile" component={StitchUserProfileScreen} />
        {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
        <Stack.Screen name="Notifications" component={StitchNotificationCenterScreen} />
        {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
        <Stack.Screen name="Settings" component={StitchUserSettingsScreen} />
        {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
        <Stack.Screen name="Submissions" component={StitchSubmissionHistoryScreen} />
        {/* @ts-expect-error - React 19 ReactNode tipi uyumsuzluğu */}
        <Stack.Screen name="Help" component={StitchHelpSupportScreen} />
    </Stack.Navigator>
);

const AppContent = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { isHealthy, isLoading: healthLoading, responseTime } = useApiHealthCheck();
  useNotifications(); // Initialize push notification handling

  const [isLocked, setIsLocked] = useState(false);
  const [initialUrl, setInitialUrl] = useState<string | null>(null);
  const appState = useRef(AppState.currentState);
  const navigationRef = useRef<any>(null);

  // Service Worker kaydı (PWA için) - Optional, hata olsa bile devam et
  useEffect(() => {
    if (Platform.OS === 'web' && 'serviceWorker' in navigator) {
      // Service Worker kaydını non-blocking yap (hata olsa bile app çalışsın)
      const registerServiceWorker = async () => {
        try {
          const registration = await navigator.serviceWorker.register('/service-worker.js');
          logger.info('✅ Service Worker registered successfully', { registration });
        } catch (error: unknown) {
          // Service Worker hatası kritik değil, sadece log'la
          // Cloudflare Pages'de dosya yoksa veya MIME type yanlışsa bu normal
          logger.warn('⚠️ Service Worker registration failed (non-critical)', {
            error: error?.message || String(error),
            note: 'App will continue to work without service worker'
          });
        }
      };
      
      // window.load event'i beklemek yerine direkt kaydet (daha hızlı)
      if (document.readyState === 'complete') {
        registerServiceWorker();
      } else {
        window.addEventListener('load', registerServiceWorker);
      }
    }
  }, []);

  // API health check ve loading durumu
  const isApiWakingUp = healthLoading && responseTime === null;
  const isWakingUp = responseTime !== null && responseTime > 10000; // 10 saniyeden uzunsa uyku modundan uyanıyor

  // Deep link handling
  useEffect(() => {
    // Handle deep links when app is already open
    const handleDeepLink = (event: { url: string }) => {
      const { path, queryParams } = Linking.parse(event.url);
      
      logger.info('🔗 Deep link received', { url: event.url, path, queryParams });
      
      // Handle invite link: verifygate://invite/TOKEN
      if (path === 'invite' && queryParams?.token) {
        const token = queryParams.token as string;
        logger.info('📧 Invitation token received', { token });
        
        // Navigate to InviteAccept screen
        if (navigationRef.current) {
          navigationRef.current.navigate('Auth', {
            screen: 'InviteAccept',
            params: { token }
          });
        }
      }
    };

    // Get initial URL (when app opens from deep link)
    Linking.getInitialURL().then((url) => {
      if (url) {
        logger.info('🚀 Initial URL', { url });
        setInitialUrl(url);
        handleDeepLink({ url });
      }
    });

    // Listen for deep links while app is running
    const subscription = Linking.addEventListener('url', handleDeepLink);

    return () => {
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      // If the app comes to the foreground from the background, and a user is logged in
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active' &&
        user
      ) {
        setIsLocked(true);
      }
      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [user]);

  // Only block on auth loading (critical)
  // Health check is non-blocking - app can start even if health check is pending
  if (authLoading) {
    return (
      <AppLoadingScreen 
        message={isApiWakingUp ? 'Sunucuya bağlanılıyor...' : 'Uygulama başlatılıyor...'}
        isWakingUp={isWakingUp}
      />
    );
  }

  // API sağlıksızsa uyarı göster ama devam et
  if (!isHealthy && !healthLoading) {
    // Log warning ama uygulamayı çalıştır
    console.warn('API health check failed, but continuing...');
  }

  // If app is locked, show the lock screen on top of everything
  if (isLocked && user) {
      return <AppLockScreen onUnlock={() => setIsLocked(false)} />;
  }
  
  const isAdmin = user && (user.role === 'super-admin' || user.role === 'company-admin');

  return (
    <>
    <NavigationContainer ref={navigationRef}>
        {/* @ts-expect-error - React Navigation React 19 uyumsuzluğu: id property gerekli */}
        <Stack.Navigator 
          screenOptions={{
            headerShown: false,
            ...TransitionPresets.SlideFromRightIOS,
            transitionSpec: {
              open: {
                animation: 'timing',
                config: {
                  duration: 300,
                  useNativeDriver: Platform.OS !== 'web',
                },
              },
              close: {
                animation: 'timing',
                config: {
                  duration: 250,
                  useNativeDriver: Platform.OS !== 'web',
                },
              },
            },
          }}
        >
          {!user ? (
            <Stack.Screen name="Auth" component={AuthStack} />
          ) : isAdmin ? (
            <Stack.Screen name="Admin" component={AdminStack} />
          ) : (
            <Stack.Screen name="App" component={UserStack} />
          )}
          <Stack.Screen name="KycFlow" component={KycStack} options={{ presentation: 'modal' }} />
        </Stack.Navigator>
    </NavigationContainer>
      {/* PWA Install Button - Sadece web platformunda göster */}
      {Platform.OS === 'web' && <InstallPWAButton />}
    </>
  );
};


const App = () => {
  return (
    <SafeAreaProvider>
    <LocalizationProvider>
      <ToastProvider>
        <AuthProvider>
          <KycProvider>
            <AppContent />
            <Toast />
          </KycProvider>
        </AuthProvider>
      </ToastProvider>
    </LocalizationProvider>
    </SafeAreaProvider>
  );
};

export default App;
